// THE CORRESPONDENCE LANGUAGE — one knob on the client, inherited by each engagement (and
// changeable there), that decides the language of EVERYTHING produced inside the engagement:
// what the agent proposes, what the descriptor writes on the rail, and the few lines the server
// itself writes into the register on the operator's behalf. Shared by server and client so the
// vocabulary and the labels are one.

export const LANGUAGES = ['de', 'en', 'fr', 'it'] as const;
/** The CONTENT translation targets — every engagement artifact exists (or is on its way to
 *  existing) in these three. 'it' is a valid SOURCE (correspondence language) but no target. */
export const TRANSLATION_LANGS = ['de', 'en', 'fr'] as const;
export type TranslationLang = (typeof TRANSLATION_LANGS)[number];
export type Language = (typeof LANGUAGES)[number];

/** The label a person reads in the UI — in the language itself. */
export const LANGUAGE_LABEL: Record<Language, string> = { de: 'Deutsch', en: 'English', fr: 'Français', it: 'Italiano' };
/** The name the (English-instructed) agent reads. */
export const LANGUAGE_NAME_EN: Record<Language, string> = { de: 'German', en: 'English', fr: 'French', it: 'Italian' };

export const isLanguage = (v: unknown): v is Language =>
  typeof v === 'string' && (LANGUAGES as readonly string[]).includes(v);

/** The one rule every agent prompt carries — AGENTS.md, every pass, every conversation. */
export function languageRule(lang: Language): string {
  const name = LANGUAGE_NAME_EN[lang];
  return [
    `LANGUAGE: ${name} (${lang}). The correspondence language of this engagement is ${name}.`,
    `EVERYTHING you write for the register is written in ${name}: statements, rationales, names,`,
    'descriptions, definitions of done, acceptance criteria, test steps, questions, conflict',
    'summaries, closing accounts. Use the wording of the source documents wherever they carry it',
    `(a term the client uses stays the client's term). \`quoted_span\` stays verbatim in whatever`,
    'language the source uses — it is evidence, not prose. Tool names, field names and refs stay',
    'as the tools require.',
  ].join('\n');
}

/** The few strings the SERVER writes into the register on the operator's behalf. */
export const SERVER_STRINGS = {
  refine_with_client: (lang: Language, ref: string, excerpt: string): string => ({
    de: `${ref} mit dem Kunden klären: ${excerpt}`,
    en: `Refine ${ref} with the client: ${excerpt}`,
    fr: `Préciser ${ref} avec le client : ${excerpt}`,
    it: `Chiarire ${ref} con il cliente: ${excerpt}`,
  })[lang],
  flagged_discuss: (lang: Language, proposalId: number | string, kind: string): string => ({
    de: `Vom Operator beim Übernehmen des Vorschlags #${proposalId} (${kind}) als «mit dem Kunden besprechen» markiert.`,
    en: `Flagged 'discuss with client' by the operator while accepting proposal #${proposalId} (${kind}).`,
    fr: `Marqué « à discuter avec le client » par l'opérateur lors de l'acceptation de la proposition n° ${proposalId} (${kind}).`,
    it: `Contrassegnato «da discutere con il cliente» dall'operatore durante l'accettazione della proposta #${proposalId} (${kind}).`,
  })[lang],
};
